/**
 * BookingCalendar.js - Operational Planning Calendar View
 * 
 * Multi-property calendar with color-coded booking statuses
 * Inspired by mobile calendar screenshot design
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  ChevronLeft, ChevronRight, Calendar as CalendarIcon, 
  Phone, Mail, User, Users, Baby, Dog, Euro, Key, 
  MessageCircle, ExternalLink, Home, Globe, CreditCard,
  Check, X, Plus, Lock, Wrench, Eye, EyeOff,
  Plane, PlaneLanding, Filter, TrendingUp, Percent
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from './ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

// ============================================================================
// CONSTANTS
// ============================================================================

const WEEKDAYS = ['L', 'M', 'M', 'G', 'V', 'S', 'D'];
const WEEKDAYS_FULL = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
const MONTHS = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
];

const STATUS_COLORS = {
  available: 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200',
  booked: 'bg-red-500 text-white',
  blocked: 'bg-gray-400 text-white',
  arrival: 'bg-orange-500 text-white',
  departure: 'bg-blue-500 text-white',
  turnover: 'bg-purple-500 text-white', // Same-day checkout + checkin
  maintenance: 'bg-gray-600 text-white',
};

const SOURCE_COLORS = {
  'booking.com': '#003580',
  'airbnb': '#FF5A5F',
  'direct': '#C5A059',
  'facebook': '#1877F2',
  'manual': '#6B7280',
};

const SOURCE_LABELS = {
  'booking.com': 'Booking.com',
  'airbnb': 'Airbnb',
  'direct': 'Diretto',
  'facebook': 'Facebook',
  'manual': 'Manuale',
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const formatDate = (date) => {
  const d = new Date(date);
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatDateShort = (date) => {
  const d = new Date(date);
  return d.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
};

const isSameDay = (d1, d2) => {
  const date1 = new Date(d1);
  const date2 = new Date(d2);
  return date1.getFullYear() === date2.getFullYear() &&
         date1.getMonth() === date2.getMonth() &&
         date1.getDate() === date2.getDate();
};

const isToday = (date) => isSameDay(date, new Date());

const getDaysInMonth = (year, month) => {
  return new Date(year, month + 1, 0).getDate();
};

const getFirstDayOfMonth = (year, month) => {
  const day = new Date(year, month, 1).getDay();
  return day === 0 ? 6 : day - 1; // Monday = 0
};

const getNights = (arrival, departure) => {
  const a = new Date(arrival);
  const d = new Date(departure);
  return Math.ceil((d - a) / (1000 * 60 * 60 * 24));
};

// ============================================================================
// MINI CALENDAR COMPONENT (For each property) - COMPACT VERSION
// ============================================================================

const MiniCalendar = ({ 
  unit, 
  bookings, 
  blocks,
  prices,
  year, 
  month, 
  onDayClick, 
  onDayLongPress,
  showPrices,
  selectedDate,
  onCreateBooking,
  onBlockDates
}) => {
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const today = new Date();
  
  // Build calendar grid
  const days = [];
  
  // Empty cells for days before the first day of month
  for (let i = 0; i < firstDay; i++) {
    days.push(null);
  }
  
  // Days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    days.push(day);
  }
  
  // Get booking status for a specific day
  const getDayStatus = (day) => {
    if (!day) return null;
    
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const date = new Date(year, month, day);
    
    // Check blocks
    const block = blocks.find(b => {
      if (b.unit_id !== unit.id) return false;
      const blockStart = new Date(b.data_inizio);
      const blockEnd = new Date(b.data_fine);
      return date >= blockStart && date <= blockEnd;
    });
    
    if (block) {
      return { 
        status: block.motivo?.toLowerCase().includes('manutenz') ? 'maintenance' : 'blocked',
        block 
      };
    }
    
    // Check bookings
    const unitBookings = bookings.filter(b => b.unit_id === unit.id);
    
    // Find arrival on this day
    const arrival = unitBookings.find(b => isSameDay(b.data_arrivo, date));
    // Find departure on this day
    const departure = unitBookings.find(b => isSameDay(b.data_partenza, date));
    // Find if day is within a booking (excluding arrival and departure days)
    const occupied = unitBookings.find(b => {
      const arrDate = new Date(b.data_arrivo);
      const depDate = new Date(b.data_partenza);
      return date > arrDate && date < depDate;
    });
    
    // Same-day turnover (checkout + checkin)
    if (arrival && departure) {
      return { 
        status: 'turnover', 
        arrival: arrival, 
        departure: departure,
        bookings: [departure, arrival]
      };
    }
    
    if (arrival) {
      return { status: 'arrival', booking: arrival };
    }
    
    // IMPORTANTE: Giorno di partenza SENZA nuovo arrivo = DISPONIBILE
    // Il checkout libera la stanza, quindi è disponibile per nuove prenotazioni
    if (departure && !arrival) {
      return { status: 'available', isDepartureDay: true, departureBooking: departure };
    }
    
    if (occupied) {
      return { status: 'booked', booking: occupied };
    }
    
    return { status: 'available' };
  };
  
  // Get price for a day
  const getDayPrice = (day) => {
    if (!day || !showPrices) return null;
    
    const date = new Date(year, month, day);
    
    // Check specific price periods
    const period = prices.find(p => {
      if (p.unit_id !== unit.id) return false;
      const start = new Date(p.data_inizio);
      const end = new Date(p.data_fine);
      return date >= start && date <= end;
    });
    
    if (period) return period.prezzo;
    
    // Return base price
    return unit.prezzo_base;
  };
  
  // Long press handler
  const [pressTimer, setPressTimer] = useState(null);
  const [pressedDay, setPressedDay] = useState(null);
  
  const handleMouseDown = (day) => {
    setPressedDay(day);
    const timer = setTimeout(() => {
      if (onDayLongPress) {
        onDayLongPress(unit, year, month, day);
      }
    }, 500);
    setPressTimer(timer);
  };
  
  const handleMouseUp = () => {
    if (pressTimer) {
      clearTimeout(pressTimer);
      setPressTimer(null);
    }
  };
  
  return (
    <div className="flex flex-col" data-testid={`calendar-${unit.id}`}>
      {/* Weekday Headers - Compact */}
      <div className="grid grid-cols-7 mb-1">
        {WEEKDAYS.map((day, idx) => (
          <div key={idx} className="text-center text-[10px] font-medium text-gray-400 py-0.5">
            {day}
          </div>
        ))}
      </div>
      
      {/* Calendar Grid - Compact */}
      <div className="grid grid-cols-7 gap-0.5">
        {days.map((day, idx) => {
          if (!day) {
            return <div key={idx} className="aspect-square" />;
          }
          
          const dayStatus = getDayStatus(day);
          const price = getDayPrice(day);
          const date = new Date(year, month, day);
          const isCurrentDay = isToday(date);
          const isSelected = selectedDate && isSameDay(date, selectedDate);
          
          // Color classes matching the screenshot style
          let bgClass = 'bg-emerald-100 text-emerald-700'; // Available = light green
          let textClass = '';
          
          if (dayStatus) {
            switch (dayStatus.status) {
              case 'booked':
              case 'arrival':
                bgClass = 'bg-red-200 text-red-700'; // Booked = pink/red
                break;
              case 'turnover':
                bgClass = 'bg-gradient-to-br from-red-200 to-emerald-100';
                textClass = 'text-gray-700';
                break;
              case 'blocked':
              case 'maintenance':
                bgClass = 'bg-gray-300 text-gray-600';
                break;
              case 'available':
              default:
                // Giorno disponibile = verde
                // Se è anche giorno di partenza, resta verde (la stanza si libera)
                bgClass = 'bg-emerald-100 text-emerald-700';
            }
          }
          
          // Today gets special styling
          if (isCurrentDay) {
            bgClass = 'bg-[#C5A059] text-white';
          }
          
          return (
            <button
              key={idx}
              onClick={() => onDayClick && onDayClick(unit, year, month, day, dayStatus)}
              onMouseDown={() => handleMouseDown(day)}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onTouchStart={() => handleMouseDown(day)}
              onTouchEnd={handleMouseUp}
              className={`
                aspect-square flex items-center justify-center rounded-md
                text-xs font-medium cursor-pointer transition-all
                ${bgClass} ${textClass}
                ${isSelected ? 'ring-2 ring-blue-500' : ''}
                active:scale-95
              `}
              data-testid={`day-${unit.id}-${year}-${month}-${day}`}
            >
              {day}
            </button>
          );
        })}
      </div>
      
      {/* Unit Name - Below calendar like in screenshot */}
      <div className="mt-2 text-sm font-medium text-gray-700 truncate">
        {unit.nome || unit.name}
      </div>
    </div>
  );
};

// ============================================================================
// BOOKING DETAIL SHEET
// ============================================================================

const BookingDetailSheet = ({ 
  isOpen, 
  onClose, 
  booking, 
  unit,
  onOpenBooking,
  onWhatsApp,
  onCall,
  onChangeRoom,
  onOpenTimeline
}) => {
  if (!booking) return null;
  
  const nights = getNights(booking.data_arrivo, booking.data_partenza);
  const sourceColor = SOURCE_COLORS[booking.source?.toLowerCase()] || SOURCE_COLORS.manual;
  
  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="bottom" className="h-[85vh] rounded-t-3xl">
        <SheetHeader className="text-left pb-4 border-b">
          <div className="flex items-center justify-between">
            <div>
              <SheetTitle className="text-xl">
                {booking.nome_ospite || 'Ospite'}
              </SheetTitle>
              <SheetDescription className="flex items-center gap-2 mt-1">
                <span 
                  className="px-2 py-0.5 rounded text-xs text-white font-medium"
                  style={{ backgroundColor: sourceColor }}
                >
                  {SOURCE_LABELS[booking.source?.toLowerCase()] || booking.source || 'Diretto'}
                </span>
                <span className="text-gray-500">{booking.codice_prenotazione}</span>
              </SheetDescription>
            </div>
            {booking.nationality_flag && (
              <span className="text-3xl">{booking.nationality_flag}</span>
            )}
          </div>
        </SheetHeader>
        
        <div className="overflow-y-auto py-4 space-y-4">
          {/* Quick Actions */}
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              className="flex-1"
              onClick={() => onWhatsApp && onWhatsApp(booking)}
            >
              <MessageCircle className="w-4 h-4 mr-2 text-green-600" />
              WhatsApp
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              className="flex-1"
              onClick={() => onCall && onCall(booking)}
            >
              <Phone className="w-4 h-4 mr-2 text-blue-600" />
              Chiama
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              className="flex-1"
              onClick={() => onOpenBooking && onOpenBooking(booking)}
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Apri
            </Button>
          </div>
          
          {/* Guest Info */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-3">
            <h4 className="font-medium text-gray-700 flex items-center gap-2">
              <Users className="w-4 h-4" />
              Ospiti
            </h4>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="bg-white rounded-lg p-2">
                <User className="w-4 h-4 mx-auto text-gray-400 mb-1" />
                <span className="text-lg font-medium">{booking.num_ospiti || booking.adulti || 2}</span>
                <span className="text-xs text-gray-500 block">Adulti</span>
              </div>
              <div className="bg-white rounded-lg p-2">
                <Baby className="w-4 h-4 mx-auto text-gray-400 mb-1" />
                <span className="text-lg font-medium">{booking.bambini || 0}</span>
                <span className="text-xs text-gray-500 block">Bambini</span>
              </div>
              <div className="bg-white rounded-lg p-2">
                <Dog className="w-4 h-4 mx-auto text-gray-400 mb-1" />
                <span className="text-lg font-medium">{booking.animali || 0}</span>
                <span className="text-xs text-gray-500 block">Animali</span>
              </div>
            </div>
          </div>
          
          {/* Contact Info */}
          <div className="space-y-2">
            {booking.telefono_ospite && (
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Phone className="w-4 h-4 text-gray-400" />
                <span className="flex-1">{booking.telefono_ospite}</span>
                <Button variant="ghost" size="sm" onClick={() => onCall && onCall(booking)}>
                  Chiama
                </Button>
              </div>
            )}
            {booking.email_ospite && (
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Mail className="w-4 h-4 text-gray-400" />
                <span className="flex-1 text-sm truncate">{booking.email_ospite}</span>
              </div>
            )}
          </div>
          
          {/* Stay Details */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-3">
            <h4 className="font-medium text-gray-700 flex items-center gap-2">
              <CalendarIcon className="w-4 h-4" />
              Soggiorno
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-xs text-gray-500 block">Alloggio</span>
                <span className="font-medium flex items-center gap-1">
                  <Home className="w-3 h-3" />
                  {unit?.nome || unit?.name || booking.nome_alloggio || 'N/A'}
                </span>
              </div>
              <div>
                <span className="text-xs text-gray-500 block">Notti</span>
                <span className="font-medium">{nights}</span>
              </div>
              <div>
                <span className="text-xs text-gray-500 block">Arrivo</span>
                <span className="font-medium text-orange-600">{formatDate(booking.data_arrivo)}</span>
              </div>
              <div>
                <span className="text-xs text-gray-500 block">Partenza</span>
                <span className="font-medium text-blue-600">{formatDate(booking.data_partenza)}</span>
              </div>
            </div>
          </div>
          
          {/* Payment */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-3">
            <h4 className="font-medium text-gray-700 flex items-center gap-2">
              <Euro className="w-4 h-4" />
              Pagamento
            </h4>
            <div className="flex items-center justify-between">
              <span className="text-2xl font-bold">
                {booking.prezzo_totale ? `€${booking.prezzo_totale}` : 'N/A'}
              </span>
              <Badge variant={booking.payment_status === 'paid' ? 'default' : 'secondary'}>
                {booking.payment_status === 'paid' ? 'Pagato' : 'In attesa'}
              </Badge>
            </div>
          </div>
          
          {/* Check-in Status */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-gray-400" />
              <span>Check-in Completato</span>
            </div>
            {booking.checkin_completed ? (
              <Badge className="bg-green-100 text-green-700">Completato</Badge>
            ) : (
              <Badge variant="secondary">In attesa</Badge>
            )}
          </div>
          
          {/* Entry Code */}
          {(booking.codice_ingresso || booking.entry_code) && (
            <div className="flex items-center justify-between p-4 bg-[#C5A059]/10 rounded-xl">
              <div className="flex items-center gap-2">
                <Key className="w-4 h-4 text-[#C5A059]" />
                <span>Codice Ingresso</span>
              </div>
              <span className="text-xl font-mono font-bold text-[#C5A059]">
                {booking.codice_ingresso || booking.entry_code}
              </span>
            </div>
          )}
          
          {/* Change Room Button */}
          <Button 
            variant="outline" 
            className="w-full"
            onClick={() => onChangeRoom && onChangeRoom(booking)}
          >
            <Home className="w-4 h-4 mr-2" />
            Cambia Alloggio
          </Button>
          
          {/* Timeline Button */}
          <Button 
            variant="ghost" 
            className="w-full"
            onClick={() => onOpenTimeline && onOpenTimeline(booking)}
          >
            <MessageCircle className="w-4 h-4 mr-2" />
            Vedi Comunicazioni
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

// ============================================================================
// LONG PRESS ACTION SHEET
// ============================================================================

const LongPressActionSheet = ({
  isOpen,
  onClose,
  unit,
  date,
  onCreateBooking,
  onBlockDates,
  onChangePrice,
  onMaintenance
}) => {
  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="bottom" className="h-auto rounded-t-3xl">
        <SheetHeader className="text-left pb-4">
          <SheetTitle>
            {unit?.nome || unit?.name} - {date ? formatDate(date) : ''}
          </SheetTitle>
        </SheetHeader>
        
        <div className="grid grid-cols-2 gap-3 pb-6">
          <Button 
            variant="outline" 
            className="h-20 flex-col gap-2"
            onClick={() => { onCreateBooking && onCreateBooking(unit, date); onClose(); }}
          >
            <Plus className="w-6 h-6 text-green-600" />
            <span>Nuova Prenotazione</span>
          </Button>
          <Button 
            variant="outline" 
            className="h-20 flex-col gap-2"
            onClick={() => { onBlockDates && onBlockDates(unit, date); onClose(); }}
          >
            <Lock className="w-6 h-6 text-gray-600" />
            <span>Blocca Date</span>
          </Button>
          <Button 
            variant="outline" 
            className="h-20 flex-col gap-2"
            onClick={() => { onChangePrice && onChangePrice(unit, date); onClose(); }}
          >
            <Euro className="w-6 h-6 text-blue-600" />
            <span>Modifica Prezzo</span>
          </Button>
          <Button 
            variant="outline" 
            className="h-20 flex-col gap-2"
            onClick={() => { onMaintenance && onMaintenance(unit, date); onClose(); }}
          >
            <Wrench className="w-6 h-6 text-orange-600" />
            <span>Manutenzione</span>
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

// ============================================================================
// STATS BAR
// ============================================================================

const StatsBar = ({ bookings, units, blocks, year, month }) => {
  const stats = useMemo(() => {
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    const daysInMonth = getDaysInMonth(year, month);
    
    const arrivalsToday = bookings.filter(b => b.data_arrivo?.startsWith(todayStr)).length;
    const departuresToday = bookings.filter(b => b.data_partenza?.startsWith(todayStr)).length;
    
    // Calculate occupancy for the month
    let totalOccupiedDays = 0;
    let totalRevenue = 0;
    let totalAvailableDays = units.length * daysInMonth;
    
    // Count blocked days
    let blockedDays = 0;
    blocks.forEach(block => {
      const start = new Date(block.data_inizio);
      const end = new Date(block.data_fine);
      const monthStart = new Date(year, month, 1);
      const monthEnd = new Date(year, month, daysInMonth);
      
      if (start <= monthEnd && end >= monthStart) {
        const effectiveStart = start < monthStart ? monthStart : start;
        const effectiveEnd = end > monthEnd ? monthEnd : end;
        const days = Math.ceil((effectiveEnd - effectiveStart) / (1000 * 60 * 60 * 24)) + 1;
        blockedDays += days;
      }
    });
    
    bookings.forEach(booking => {
      const arrival = new Date(booking.data_arrivo);
      const departure = new Date(booking.data_partenza);
      const monthStart = new Date(year, month, 1);
      const monthEnd = new Date(year, month, daysInMonth);
      
      if (arrival <= monthEnd && departure >= monthStart) {
        const effectiveStart = arrival < monthStart ? monthStart : arrival;
        const effectiveEnd = departure > monthEnd ? monthEnd : departure;
        const nights = Math.ceil((effectiveEnd - effectiveStart) / (1000 * 60 * 60 * 24));
        totalOccupiedDays += nights;
        
        // Estimate revenue for this month's portion
        if (booking.prezzo_totale) {
          const totalNights = getNights(booking.data_arrivo, booking.data_partenza);
          const pricePerNight = booking.prezzo_totale / totalNights;
          totalRevenue += pricePerNight * nights;
        }
      }
    });
    
    const freeNights = totalAvailableDays - totalOccupiedDays - blockedDays;
    const occupancyRate = totalAvailableDays > 0 
      ? Math.round((totalOccupiedDays / totalAvailableDays) * 100) 
      : 0;
    
    return {
      arrivalsToday,
      departuresToday,
      occupancyRate,
      revenue: Math.round(totalRevenue),
      freeNights: Math.max(0, freeNights)
    };
  }, [bookings, units, blocks, year, month]);
  
  return (
    <div className="grid grid-cols-5 gap-2 text-center" data-testid="calendar-stats">
      <div className="bg-orange-50 rounded-lg p-2">
        <PlaneLanding className="w-4 h-4 mx-auto text-orange-500 mb-1" />
        <span className="text-lg font-bold text-orange-600">{stats.arrivalsToday}</span>
        <span className="text-[10px] text-gray-500 block">Arrivi oggi</span>
      </div>
      <div className="bg-blue-50 rounded-lg p-2">
        <Plane className="w-4 h-4 mx-auto text-blue-500 mb-1" />
        <span className="text-lg font-bold text-blue-600">{stats.departuresToday}</span>
        <span className="text-[10px] text-gray-500 block">Partenze oggi</span>
      </div>
      <div className="bg-purple-50 rounded-lg p-2">
        <Percent className="w-4 h-4 mx-auto text-purple-500 mb-1" />
        <span className="text-lg font-bold text-purple-600">{stats.occupancyRate}%</span>
        <span className="text-[10px] text-gray-500 block">Occupazione</span>
      </div>
      <div className="bg-green-50 rounded-lg p-2">
        <Euro className="w-4 h-4 mx-auto text-green-500 mb-1" />
        <span className="text-lg font-bold text-green-600">{stats.revenue}</span>
        <span className="text-[10px] text-gray-500 block">Revenue</span>
      </div>
      <div className="bg-emerald-50 rounded-lg p-2">
        <CalendarIcon className="w-4 h-4 mx-auto text-emerald-500 mb-1" />
        <span className="text-lg font-bold text-emerald-600">{stats.freeNights}</span>
        <span className="text-[10px] text-gray-500 block">Notti libere</span>
      </div>
    </div>
  );
};

// ============================================================================
// LEGEND
// ============================================================================

const Legend = () => (
  <div className="flex flex-wrap gap-2 justify-center text-xs" data-testid="calendar-legend">
    <div className="flex items-center gap-1">
      <div className="w-3 h-3 rounded bg-emerald-100 border border-emerald-300" />
      <span>Disponibile</span>
    </div>
    <div className="flex items-center gap-1">
      <div className="w-3 h-3 rounded bg-red-500" />
      <span>Occupato</span>
    </div>
    <div className="flex items-center gap-1">
      <div className="w-3 h-3 rounded bg-orange-500" />
      <span>Arrivo</span>
    </div>
    <div className="flex items-center gap-1">
      <div className="w-3 h-3 rounded bg-blue-500" />
      <span>Partenza</span>
    </div>
    <div className="flex items-center gap-1">
      <div className="w-3 h-3 rounded bg-gradient-to-br from-blue-500 to-orange-500" />
      <span>Turnover</span>
    </div>
    <div className="flex items-center gap-1">
      <div className="w-3 h-3 rounded bg-gray-400" />
      <span>Bloccato</span>
    </div>
    <div className="flex items-center gap-1">
      <div className="w-3 h-3 rounded bg-gray-600" />
      <span>Manutenzione</span>
    </div>
  </div>
);

// ============================================================================
// MAIN BOOKING CALENDAR COMPONENT
// ============================================================================

export default function BookingCalendar({
  units = [],
  bookings = [],
  blocks = [],
  prices = [],
  onOpenBooking,
  onCreateBooking,
  onBlockDates,
  onChangePrice,
  onMaintenance,
  onChangeRoom,
  onWhatsApp,
  onCall,
  onOpenTimeline
}) {
  const today = new Date();
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [showPrices, setShowPrices] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [bookingSheetOpen, setBookingSheetOpen] = useState(false);
  const [longPressData, setLongPressData] = useState(null);
  const [longPressSheetOpen, setLongPressSheetOpen] = useState(false);
  
  // Filters
  const [filterUnit, setFilterUnit] = useState('all');
  const [filterSource, setFilterSource] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Navigation
  const goToPrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };
  
  const goToNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };
  
  const goToToday = () => {
    setCurrentYear(today.getFullYear());
    setCurrentMonth(today.getMonth());
  };
  
  // Filter units
  const filteredUnits = useMemo(() => {
    if (filterUnit === 'all') return units;
    return units.filter(u => u.id === filterUnit);
  }, [units, filterUnit]);
  
  // Filter bookings
  const filteredBookings = useMemo(() => {
    let result = bookings;
    
    if (filterSource !== 'all') {
      result = result.filter(b => b.source?.toLowerCase() === filterSource);
    }
    
    if (filterStatus === 'occupied') {
      const todayStr = today.toISOString().split('T')[0];
      result = result.filter(b => b.data_arrivo <= todayStr && b.data_partenza > todayStr);
    } else if (filterStatus === 'arriving') {
      const todayStr = today.toISOString().split('T')[0];
      result = result.filter(b => b.data_arrivo?.startsWith(todayStr));
    } else if (filterStatus === 'departing') {
      const todayStr = today.toISOString().split('T')[0];
      result = result.filter(b => b.data_partenza?.startsWith(todayStr));
    }
    
    return result;
  }, [bookings, filterSource, filterStatus, today]);
  
  // Handle day click
  const handleDayClick = (unit, year, month, day, dayStatus) => {
    if (dayStatus?.booking) {
      setSelectedBooking(dayStatus.booking);
      setSelectedUnit(unit);
      setBookingSheetOpen(true);
    } else if (dayStatus?.bookings) {
      // Turnover day - show first booking
      setSelectedBooking(dayStatus.bookings[0]);
      setSelectedUnit(unit);
      setBookingSheetOpen(true);
    }
  };
  
  // Handle long press
  const handleDayLongPress = (unit, year, month, day) => {
    const date = new Date(year, month, day);
    setLongPressData({ unit, date });
    setLongPressSheetOpen(true);
  };
  
  return (
    <div className="space-y-4" data-testid="booking-calendar">
      {/* Month Navigation - Clean like screenshot */}
      <div className="flex items-center justify-between">
        <button 
          onClick={goToPrevMonth}
          className="p-2 hover:bg-gray-100 rounded-lg transition"
        >
          <ChevronLeft className="w-5 h-5 text-gray-600" />
        </button>
        
        <h2 className="text-lg font-medium text-gray-900">
          {MONTHS[currentMonth].toLowerCase()} {currentYear}
        </h2>
        
        <button 
          onClick={goToNextMonth}
          className="p-2 hover:bg-gray-100 rounded-lg transition"
        >
          <ChevronRight className="w-5 h-5 text-gray-600" />
        </button>
      </div>
      
      {/* Calendar Grid - 2 columns on mobile, 3+ on desktop */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 px-1">
        {filteredUnits.map(unit => (
          <MiniCalendar
            key={unit.id}
            unit={unit}
            bookings={filteredBookings.filter(b => b.unit_id === unit.id)}
            blocks={blocks.filter(b => b.unit_id === unit.id)}
            prices={prices.filter(p => p.unit_id === unit.id)}
            year={currentYear}
            month={currentMonth}
            onDayClick={handleDayClick}
            onDayLongPress={handleDayLongPress}
            showPrices={showPrices}
            onCreateBooking={onCreateBooking}
            onBlockDates={onBlockDates}
          />
        ))}
      </div>
      
      {/* No units message */}
      {filteredUnits.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <CalendarIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>Nessun alloggio trovato</p>
        </div>
      )}
      
      {/* Compact Stats - Below calendars */}
      <div className="pt-4 border-t">
        <StatsBar 
          bookings={filteredBookings} 
          units={filteredUnits} 
          blocks={blocks}
          year={currentYear} 
          month={currentMonth} 
        />
      </div>
      
      {/* Legend - Compact */}
      <Legend />
      
      {/* Filters - Collapsible on mobile */}
      <details className="bg-gray-50 rounded-lg p-3">
        <summary className="flex items-center gap-2 cursor-pointer text-sm font-medium text-gray-700">
          <Filter className="w-4 h-4" />
          Filtri
        </summary>
        <div className="mt-3 flex flex-wrap gap-2">
          <Select value={filterUnit} onValueChange={setFilterUnit}>
            <SelectTrigger className="w-[130px] h-8 text-xs">
              <SelectValue placeholder="Alloggio" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti</SelectItem>
              {units.map(u => (
                <SelectItem key={u.id} value={u.id}>{u.nome || u.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={filterSource} onValueChange={setFilterSource}>
            <SelectTrigger className="w-[110px] h-8 text-xs">
              <SelectValue placeholder="Fonte" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte</SelectItem>
              <SelectItem value="booking.com">Booking</SelectItem>
              <SelectItem value="airbnb">Airbnb</SelectItem>
              <SelectItem value="direct">Diretto</SelectItem>
            </SelectContent>
          </Select>
          
          <div className="flex items-center gap-2 ml-auto">
            <Switch
              id="show-prices"
              checked={showPrices}
              onCheckedChange={setShowPrices}
              className="scale-75"
            />
            <Label htmlFor="show-prices" className="text-xs cursor-pointer">
              Prezzi
            </Label>
          </div>
          
          <Button variant="outline" size="sm" onClick={goToToday} className="h-8 text-xs">
            Oggi
          </Button>
        </div>
      </details>
      
      {/* Booking Detail Sheet */}
      <BookingDetailSheet
        isOpen={bookingSheetOpen}
        onClose={() => setBookingSheetOpen(false)}
        booking={selectedBooking}
        unit={selectedUnit}
        onOpenBooking={onOpenBooking}
        onWhatsApp={onWhatsApp}
        onCall={onCall}
        onChangeRoom={onChangeRoom}
        onOpenTimeline={onOpenTimeline}
      />
      
      {/* Long Press Action Sheet */}
      <LongPressActionSheet
        isOpen={longPressSheetOpen}
        onClose={() => setLongPressSheetOpen(false)}
        unit={longPressData?.unit}
        date={longPressData?.date}
        onCreateBooking={onCreateBooking}
        onBlockDates={onBlockDates}
        onChangePrice={onChangePrice}
        onMaintenance={onMaintenance}
      />
    </div>
  );
}
